import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { GeoniusVesselData } from '../../api/vessels';
import { VesselList } from '../../common-components/manage-vessel-lists/vessel-lists.model';

export enum EAdminMenuItems {
  USERS = 'Users',
  VESSELS = 'Vessels',
  VESSEL_LISTS = 'Vessel_Lists',
  ALERT_CONFIG = 'Alert_Config',
}

export enum EAdminOptionsMenuItems {
  COMPANY_MANAGEMENT = 'Company_Management',
}

export enum EUserMenuItems {
  USER = 'User',
}

export enum EUserMenuItemsRI {
  USER_ACCESS = 'User Access',
}

export enum FeatureNames {
  fleet_license = 'Fleet',
  ports_license = 'Ports',
  cruise_license = 'Cruise',
  sanctions = 'Sanctions',
  ais_history = 'AIS History',
  voyager_intel_tool = 'Voyager Intel Tool',
}

export interface License {
  total: number | 'default';
  available: number | 'default';
}

export interface FeatureLicenses {
  seats: License;
  licenses?: Partial<Record<keyof typeof FeatureNames, License>>;
}

export interface Company {
  company_id: string;
  company_name: string;
  tenant_id: string;
  feature_licenses?: FeatureLicenses;
  is_suspended: boolean;
}
export interface User {
  sub: string;
  username: string;
  name: string;
  email: string;
  groups: string[];
  company_id: string;
  tenant_id: string;
  last_login: string;
}

export type FailedUser = {
  first_name: string;
  last_name: string;
  username: string;
};

interface AdminState {
  companiesLoading: boolean;
  companiesError: boolean;
  companies: Company[] | null;
  allVesselsLoading: boolean;
  allVesselsError: boolean;
  allVessels: GeoniusVesselData[] | null;
  usersLoading: boolean;
  usersError: boolean;
  users: Record<Company['company_id'], User[] | undefined>;
  selectedOption: EAdminMenuItems;
  selectedCompany: Company | null;
  selectedList: VesselList | null;
  selectedUser: User | null;
}

export const INITIAL_ADMIN_STATE: AdminState = {
  companiesLoading: false,
  companiesError: false,
  companies: null,
  allVesselsLoading: false,
  allVesselsError: false,
  allVessels: null,
  usersLoading: false,
  usersError: false,
  users: {},
  selectedOption: EAdminMenuItems.USERS,
  selectedCompany: null,
  selectedList: null,
  selectedUser: null,
};

const AdminSlice = createSlice({
  name: 'admin',
  initialState: INITIAL_ADMIN_STATE,
  reducers: {
    setCompaniesLoading: (state) => {
      state.companiesLoading = true;
    },
    setCompaniesError: (state) => {
      state.companiesError = true;
      state.companiesLoading = false;
    },
    setCompanies: (state, action) => {
      state.companiesLoading = false;
      state.companiesError = false;
      state.companies = action.payload;
    },
    setSelectedCompany: (state, action) => {
      state.selectedCompany = action.payload;
    },
    setSelectedCompanySeats: (state, action) => {
      state.selectedCompany!.feature_licenses!.seats.total = action.payload;
    },
    setSelectedList: (state, action) => {
      state.selectedList = action.payload;
    },
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    setCompanyUsers: (
      state,
      action: PayloadAction<{ companyId: Company['company_id']; users: User[] }>
    ) => {
      const { companyId, users } = action.payload;
      state.users[companyId] = users;
    },
  },
});

export const {
  setCompaniesLoading,
  setCompaniesError,
  setCompanies,
  setSelectedCompany,
  setSelectedList,
  setSelectedUser,
  setCompanyUsers,
  setSelectedCompanySeats,
} = AdminSlice.actions;

export default AdminSlice.reducer;
